import React, { useState, useEffect } from 'react';
import { api } from '../api';

const TYPE_OPTIONS = [
  { value: 'commissioned',   label: 'Commissioned' },
  { value: 'hourly',         label: 'Hourly' },
  { value: 'office_cleaner', label: 'Office Cleaner' },
  { value: 'admin',          label: 'Admin' },
];

const TYPE_STYLE = {
  admin:          { bg: '#f3f0ff', color: '#7c3aed', border: '#7c3aed40' },
  commissioned:   { bg: '#fffbeb', color: '#d4a800', border: '#F5C51840' },
  hourly:         { bg: '#d1fae5', color: '#065f46', border: '#06b05a40' },
  office_cleaner: { bg: '#e8f2f7', color: '#3a6275', border: '#4A7A9140' },
};

const EMPTY_FORM = {
  username: '', password: '', name: '', type: 'commissioned', daily_wage: 28.50,
  start_date: '', birthday: '', email: '', phone: '', abn: '', bsb: '', account_number: '', zoho_vendor_name: '',
};

export default function ContractorManagement() {
  const [contractors, setContractors] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [addMode, setAddMode] = useState('manual'); // 'manual' | 'link'
  const [form, setForm] = useState(EMPTY_FORM);
  const [signupUrl, setSignupUrl] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editTab, setEditTab] = useState('basic');

  // Checklist management
  const [checklistTasks, setChecklistTasks] = useState([]);
  const [showChecklist, setShowChecklist] = useState(false);
  const [newTask, setNewTask] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    const [data, tasks] = await Promise.all([api.getContractors(), api.getChecklistTasks()]);
    setContractors(data);
    setChecklistTasks(tasks);
  }

  function resetAdd() { setForm(EMPTY_FORM); setError(''); setShowAdd(false); setSignupUrl(''); setAddMode('manual'); }

  async function handleAdd(e) {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      await api.addContractor(form);
      setSuccess('Contractor added!');
      resetAdd(); load(); setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleSendLink(e) {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      const result = await api.createSignupToken({
        name: form.name, type: form.type, daily_wage: form.daily_wage,
      });
      const url = `${window.location.origin}${result.signup_path}`;
      setSignupUrl(url);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  function openEdit(c) {
    setEditingId(c.id);
    setEditTab('basic');
    setEditForm({
      username: c.username, name: c.name, type: c.type,
      daily_wage: c.daily_wage, password: '',
      start_date: c.start_date || '', birthday: c.birthday || '',
      email: c.email || '', phone: c.phone || '',
      abn: c.abn || '', bsb: c.bsb || '', account_number: c.account_number || '',
      zoho_vendor_name: c.zoho_vendor_name || '',
      photo_url: c.photo_url || '',
    });
    setEditError('');
  }

  // Read file as data URL and set as photo — resize to max 512px to keep payload small
  async function handlePhotoUpload(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setEditError('Please select an image file'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setEditError('Image must be under 5MB'); return;
    }
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      // Resize via canvas for compactness
      const img = new Image();
      img.onload = () => {
        const MAX = 400;
        let { width, height } = img;
        if (width > height) {
          if (width > MAX) { height = Math.round(height * (MAX / width)); width = MAX; }
        } else {
          if (height > MAX) { width = Math.round(width * (MAX / height)); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        const resized = canvas.toDataURL('image/jpeg', 0.85);
        setEditForm(f => ({ ...f, photo_url: resized }));
        setEditError('');
      };
      img.src = dataUrl;
    } catch (err) {
      setEditError('Could not load image: ' + err.message);
    }
  }

  async function handleEditSave(e) {
    e.preventDefault(); setEditError(''); setEditSaving(true);
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await api.updateContractor(editingId, payload);
      setSuccess('Contractor updated!');
      setEditingId(null); load(); setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setEditError(err.message); }
    finally { setEditSaving(false); }
  }

  async function handleDelete(c) {
    if (!window.confirm(`Permanently delete ${c.name}? This cannot be undone.`)) return;
    try { await api.deleteContractor(c.id); setSuccess(`${c.name} deleted.`); load(); setTimeout(() => setSuccess(''), 3000); }
    catch (err) { alert(err.message); }
  }

  async function handleToggleActive(c) {
    try { await api.updateContractor(c.id, { is_active: !c.is_active }); load(); }
    catch (err) { alert(err.message); }
  }

  async function saveChecklist() {
    await api.updateChecklistTasks(checklistTasks);
    setSuccess('Checklist saved!'); setTimeout(() => setSuccess(''), 3000);
  }

  function addChecklistTask() {
    if (!newTask.trim()) return;
    setChecklistTasks([...checklistTasks, { label: newTask.trim(), sort_order: checklistTasks.length }]);
    setNewTask('');
  }

  function removeTask(i) { setChecklistTasks(checklistTasks.filter((_, idx) => idx !== i)); }

  const editingContractor = contractors.find(c => c.id === editingId);

  const rateLabel = (c) => {
    if (c.type === 'hourly' || c.type === 'office_cleaner') return `$${c.daily_wage}/hr`;
    return '—';
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Contractor Management</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowChecklist(!showChecklist)}>
            {showChecklist ? 'Hide Checklist' : '☑ Checklist Tasks'}
          </button>
          <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}>
            {showAdd ? 'Cancel' : '+ Add Contractor'}
          </button>
        </div>
      </div>

      {success && <div className="alert alert-success">{success}</div>}

      {/* Checklist task manager */}
      {showChecklist && (
        <div className="card" style={{ marginBottom: 20, borderTop: '3px solid #F5C518' }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>Office Cleaner Checklist Tasks</h3>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
            Admin-customizable daily task list for office cleaners.
          </p>
          {checklistTasks.map((task, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ flex: 1, fontSize: 14 }}>{task.label}</span>
              <button className="btn btn-danger btn-sm" onClick={() => removeTask(i)}>✕</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <input
              className="form-group"
              style={{ flex: 1, margin: 0, padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: 0, fontSize: 14 }}
              placeholder="Add task..."
              value={newTask}
              onChange={e => setNewTask(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addChecklistTask())}
            />
            <button className="btn btn-ghost btn-sm" onClick={addChecklistTask}>+ Add</button>
            <button className="btn btn-primary btn-sm" onClick={saveChecklist}>Save</button>
          </div>
        </div>
      )}

      {/* Add Contractor form */}
      {showAdd && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button
              type="button"
              className={`btn btn-sm ${addMode === 'manual' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setAddMode('manual')}
            >Fill Manually</button>
            <button
              type="button"
              className={`btn btn-sm ${addMode === 'link' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setAddMode('link')}
            >Send Signup Link</button>
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          {addMode === 'manual' ? (
            <form onSubmit={handleAdd}>
              <h3 style={{ margin: '0 0 16px' }}>New Contractor</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Full Name *</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Username *</label>
                  <input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} required />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Password *</label>
                  <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Type *</label>
                  <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                    {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {(form.type === 'hourly' || form.type === 'office_cleaner') && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Hourly Rate ($/hr)</label>
                    <input type="number" step="0.01" value={form.daily_wage} onChange={e => setForm({ ...form, daily_wage: parseFloat(e.target.value) })} />
                  </div>
                )}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Start Date</label>
                  <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Birthday</label>
                  <input type="date" value={form.birthday} onChange={e => setForm({ ...form, birthday: e.target.value })} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Phone</label>
                  <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>ABN</label>
                  <input value={form.abn} onChange={e => setForm({ ...form, abn: e.target.value })} placeholder="XX XXX XXX XXX" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>BSB</label>
                  <input value={form.bsb} onChange={e => setForm({ ...form, bsb: e.target.value })} placeholder="000-000" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Account Number</label>
                  <input value={form.account_number} onChange={e => setForm({ ...form, account_number: e.target.value })} />
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: 16 }}>
                {saving ? 'Adding...' : 'Add Contractor'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSendLink}>
              <h3 style={{ margin: '0 0 12px' }}>Send Signup Link</h3>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
                The contractor will receive a link to fill in their own details.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Name (optional)</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Pre-fill their name" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Type *</label>
                  <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                    {TYPE_OPTIONS.filter(o => o.value !== 'admin').map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {(form.type === 'hourly' || form.type === 'office_cleaner') && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Hourly Rate ($/hr)</label>
                    <input type="number" step="0.01" value={form.daily_wage} onChange={e => setForm({ ...form, daily_wage: parseFloat(e.target.value) })} />
                  </div>
                )}
              </div>
              {!signupUrl ? (
                <button type="submit" className="btn btn-accent" disabled={saving}>
                  {saving ? 'Generating...' : '🔗 Generate Signup Link'}
                </button>
              ) : (
                <div>
                  <div style={{ background: '#f4f8fa', border: '1px solid #B8D4E0', borderRadius: 0, padding: 12, marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Share this link (valid 7 days):</div>
                    <div style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all', color: '#3a6275' }}>{signupUrl}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => navigator.clipboard.writeText(signupUrl)}>
                      Copy Link
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={resetAdd}>Done</button>
                  </div>
                </div>
              )}
            </form>
          )}
        </div>
      )}

      {/* ── Active Contractors ── */}
      <div className="card" style={{ borderTop: '3px solid #059669' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: '#065f46' }}>
            ✅ Active Contractors
            <span style={{ marginLeft: 8, background: '#d1fae5', color: '#065f46', borderRadius: 0, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
              {contractors.filter(c => c.is_active).length}
            </span>
          </h3>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Type</th>
                <th>Rate</th>
                <th>Start Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contractors.filter(c => c.is_active).map(c => {
                const ts = TYPE_STYLE[c.type] || TYPE_STYLE.commissioned;
                return (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 700, fontSize: 14 }}>{c.name}</td>
                    <td style={{ fontSize: 13, color: '#6b7280' }}>{c.username}</td>
                    <td>
                      <span style={{
                        background: ts.bg, color: ts.color,
                        border: `1px solid ${ts.border}`,
                        padding: '3px 10px', borderRadius: 0,
                        fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                      }}>
                        {c.type === 'office_cleaner' ? 'Office Cleaner' : c.type}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>{rateLabel(c)}</td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>{c.start_date || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button className="btn btn-sm btn-ghost" onClick={() => handleToggleActive(c)}>Deactivate</button>
                        {c.type !== 'admin' && (
                          <>
                            <button className="btn btn-sm btn-primary" onClick={() => openEdit(c)}>Edit</button>
                            <button className="btn btn-sm btn-danger" onClick={() => handleDelete(c)}>Delete</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Inactive Contractors — collapsed by default ── */}
      {contractors.filter(c => !c.is_active).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <button
            onClick={() => setShowInactive(!showInactive)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'transparent', border: '1px solid #e5e7eb',
              borderRadius: 0, padding: '8px 16px', cursor: 'pointer',
              fontSize: 13, color: '#9ca3af', fontWeight: 600, width: '100%',
            }}
          >
            <span style={{ fontSize: 16 }}>{showInactive ? '▾' : '▸'}</span>
            Inactive Contractors ({contractors.filter(c => !c.is_active).length})
          </button>

          {showInactive && (
            <div style={{ marginTop: 8, border: '1px solid #f3f4f6', borderRadius: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', opacity: 0.65 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 600, textAlign: 'left', color: '#9ca3af' }}>Name</th>
                    <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 600, textAlign: 'left', color: '#9ca3af' }}>Username</th>
                    <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 600, textAlign: 'left', color: '#9ca3af' }}>Type</th>
                    <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 600, textAlign: 'left', color: '#9ca3af' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contractors.filter(c => !c.is_active).map(c => {
                    const ts = TYPE_STYLE[c.type] || TYPE_STYLE.commissioned;
                    return (
                      <tr key={c.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '7px 12px', fontSize: 13, color: '#6b7280', fontWeight: 600 }}>{c.name}</td>
                        <td style={{ padding: '7px 12px', fontSize: 12, color: '#9ca3af' }}>{c.username}</td>
                        <td style={{ padding: '7px 12px' }}>
                          <span style={{
                            background: '#f3f4f6', color: '#9ca3af',
                            padding: '2px 8px', borderRadius: 0,
                            fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                          }}>
                            {c.type === 'office_cleaner' ? 'Office' : c.type}
                          </span>
                        </td>
                        <td style={{ padding: '7px 12px' }}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-sm btn-success" onClick={() => handleToggleActive(c)}>Activate</button>
                            {c.type !== 'admin' && (
                              <>
                                <button className="btn btn-sm btn-ghost" style={{ fontSize: 11 }} onClick={() => openEdit(c)}>Edit</button>
                                <button className="btn btn-sm btn-danger" style={{ fontSize: 11 }} onClick={() => handleDelete(c)}>Delete</button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Edit modal */}
      {editingId && editingContractor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div className="card" style={{ width: 540, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Edit — {editingContractor.name}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(null)}>✕</button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e5e7eb', paddingBottom: 4 }}>
              {['basic', 'profile', 'financial', 'insurance'].map(t => (
                <button key={t} type="button"
                  style={{
                    padding: '6px 14px', border: 'none', background: 'transparent',
                    fontWeight: 600, fontSize: 13, cursor: 'pointer',
                    color: editTab === t ? '#4A7A91' : '#9ca3af',
                    borderBottom: editTab === t ? '2px solid #4A7A91' : '2px solid transparent',
                  }}
                  onClick={() => setEditTab(t)}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>

            {editError && <div className="alert alert-error">{editError}</div>}

            <form onSubmit={handleEditSave}>
              {editTab === 'basic' && (
                <>
                  {/* Profile photo uploader */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: 12, marginBottom: 14,
                    background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 12,
                  }}>
                    <div style={{
                      width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
                      background: editForm.photo_url ? `#E5E7EB url(${editForm.photo_url}) center/cover no-repeat` : '#E2E8F0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    }}>
                      {!editForm.photo_url && (
                        <span style={{ fontSize: 26, color: '#94A3B8', fontWeight: 700 }}>
                          {(editForm.name || '?').charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                        Profile Photo
                      </div>
                      <div style={{ fontSize: 11, color: '#64748B', marginBottom: 6 }}>
                        Shown on dashboard payroll chips. Max 5 MB (auto-resized).
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <label style={{
                          padding: '6px 12px', background: '#4A7A91', color: '#fff',
                          borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                          display: 'inline-block',
                        }}>
                          {editForm.photo_url ? 'Change Photo' : 'Upload Photo'}
                          <input type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={e => handlePhotoUpload(e.target.files[0])} />
                        </label>
                        {editForm.photo_url && (
                          <button type="button"
                            onClick={() => setEditForm(f => ({ ...f, photo_url: '' }))}
                            style={{
                              padding: '6px 12px', background: '#fff', color: '#DC2626',
                              border: '1px solid #FCA5A5', borderRadius: 8,
                              fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            }}>
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Full Name *</label>
                    <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} required />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Username *</label>
                    <input value={editForm.username} onChange={e => setEditForm({ ...editForm, username: e.target.value })} required />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>New Password <span style={{ color: '#9ca3af', fontWeight: 400 }}>(blank = unchanged)</span></label>
                    <input type="password" value={editForm.password} onChange={e => setEditForm({ ...editForm, password: e.target.value })} placeholder="unchanged" />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Type</label>
                    <select value={editForm.type} onChange={e => setEditForm({ ...editForm, type: e.target.value })}>
                      {TYPE_OPTIONS.filter(o => o.value !== 'admin').map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  {(editForm.type === 'hourly' || editForm.type === 'office_cleaner') && (
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Hourly Rate ($/hr)</label>
                      <input type="number" step="0.01" value={editForm.daily_wage} onChange={e => setEditForm({ ...editForm, daily_wage: parseFloat(e.target.value) })} />
                    </div>
                  )}
                </div>
                </>
              )}

              {editTab === 'profile' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Start Date</label>
                    <input type="date" value={editForm.start_date} onChange={e => setEditForm({ ...editForm, start_date: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Birthday</label>
                    <input type="date" value={editForm.birthday} onChange={e => setEditForm({ ...editForm, birthday: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Email</label>
                    <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Phone</label>
                    <input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} />
                  </div>
                </div>
              )}

              {editTab === 'financial' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>ABN</label>
                    <input value={editForm.abn} onChange={e => setEditForm({ ...editForm, abn: e.target.value })} placeholder="XX XXX XXX XXX" />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>BSB</label>
                    <input value={editForm.bsb} onChange={e => setEditForm({ ...editForm, bsb: e.target.value })} placeholder="000-000" />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                    <label>Account Number</label>
                    <input value={editForm.account_number} onChange={e => setEditForm({ ...editForm, account_number: e.target.value })} />
                  </div>
                  {/* Their statement matches bank transfers by name. Set this
                      only when the transfer text doesn't carry their real name. */}
                  <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                    <label>Zoho name (only if transfers don't match)</label>
                    <input
                      value={editForm.zoho_vendor_name}
                      onChange={e => setEditForm({ ...editForm, zoho_vendor_name: e.target.value })}
                      placeholder="Leave blank — matched by name automatically"
                    />
                  </div>
                </div>
              )}

              {editTab === 'insurance' && (
                <InsurancePane subId={editingId} />
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setEditingId(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={editSaving}>
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Insurance pane (inside the Edit Contractor modal) ──────────────────────
// Lists the full insurance history for one sub, with download links and a
// "delete this upload" action for genuine mistakes. Status colour comes
// straight from the backend (computed against today's date).
const INS_TIPO_LABEL = { pl: 'Public Liability', ip: 'Personal Accident / IP' };
const INS_STATUS_META = {
  pending:  { label: 'Pending',     bg: '#eef2f6', fg: '#5b6b7b' },
  valid:    { label: 'Valid',       bg: '#dcfce7', fg: '#15803d' },
  expiring: { label: 'Expiring',    bg: '#fef3c7', fg: '#92400e' },
  expired:  { label: 'Expired',     bg: '#fee2e2', fg: '#991b1b' },
};

function InsurancePane({ subId }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');

  async function load() {
    setLoading(true);
    try {
      const r = await api.insuranceForContractor(subId);
      setRows(r);
      setErr('');
    } catch (e) {
      setErr(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (subId) load(); /* eslint-disable-next-line */ }, [subId]);

  async function handleDelete(id) {
    if (!window.confirm('Delete this certificate upload?')) return;
    try {
      await api.deleteInsuranceCertificate(id);
      load();
    } catch (e) {
      alert(e.message || 'Delete failed');
    }
  }

  // Group by tipo so the modal shows two sections (PL + IP) with the
  // newest upload at the top of each.
  const byTipo = { pl: [], ip: [] };
  rows.forEach(r => { if (byTipo[r.tipo]) byTipo[r.tipo].push(r); });

  if (loading) return <div style={{ padding: 20, color: '#6b7280', fontSize: 13 }}>Loading…</div>;
  if (err)     return <div style={{ padding: 12, background: '#FEE2E2', color: '#991B1B', borderRadius: 6, fontSize: 12 }}>{err}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {(['pl', 'ip']).map(tipo => {
        const list = byTipo[tipo];
        const latest = list[0];
        const sMeta  = INS_STATUS_META[latest?.status || 'pending'];
        return (
          <div key={tipo} style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <strong style={{ fontSize: 13, color: '#1e3a4a' }}>{INS_TIPO_LABEL[tipo]}</strong>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                background: sMeta.bg, color: sMeta.fg,
              }}>{sMeta.label}</span>
            </div>

            {list.length === 0 ? (
              <div style={{ fontSize: 12, color: '#9ca3af', padding: '6px 0' }}>
                No certificate uploaded yet.
              </div>
            ) : (
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', color: '#475569', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>Insurer</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>Policy</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>Expires</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>File</th>
                    <th style={{ padding: '4px 6px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(r => (
                    <tr key={r.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '5px 6px', color: '#0F172A' }}>{r.seguradora}</td>
                      <td style={{ padding: '5px 6px', color: '#0F172A', fontFamily: 'monospace' }}>{r.numero_apolice}</td>
                      <td style={{ padding: '5px 6px', color: '#0F172A' }}>
                        {String(r.validade).slice(0, 10).split('-').reverse().join('/')}
                      </td>
                      <td style={{ padding: '5px 6px' }}>
                        <a href={api.insuranceFileUrl(r.id)} target="_blank" rel="noreferrer"
                           style={{ color: '#4A7A91', fontWeight: 600 }}>
                          Open
                        </a>
                      </td>
                      <td style={{ padding: '5px 6px', textAlign: 'right' }}>
                        <button onClick={() => handleDelete(r.id)}
                                title="Delete this upload"
                                style={{ background: '#fff1f1', color: '#b91c1c', border: '1px solid #fca5a5', padding: '2px 6px', fontSize: 11, cursor: 'pointer', borderRadius: 4 }}>
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}
